import React, { useRef, useEffect, useState } from 'react';
import SignaturePad from 'signature_pad';
import { Trash2, Download, Check } from 'lucide-react';

interface SignatureCaptureProps {
  onSignatureChange: (signatureData: string | null) => void;
  initialSignature?: string | null;
  className?: string;
}

export const SignatureCapture: React.FC<SignatureCaptureProps> = ({
  onSignatureChange,
  initialSignature = null,
  className = ''
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [hasSignature, setHasSignature] = useState(!!initialSignature);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const signaturePad = new SignaturePad(canvas, {
        backgroundColor: 'rgba(255, 255, 255, 0)',
        penColor: 'rgb(0, 0, 0)',
        minWidth: 1,
        maxWidth: 3,
        throttle: 16,
        minDistance: 5,
      });

      signaturePadRef.current = signaturePad;

      // Cargar firma inicial si existe
      if (initialSignature) {
        signaturePad.fromDataURL(initialSignature);
        setHasSignature(true);
      }

      // Escuchar cambios en la firma
      const handleEnd = () => {
        if (!signaturePad.isEmpty()) {
          const dataURL = signaturePad.toDataURL('image/png');
          setHasSignature(true);
          onSignatureChange(dataURL);
        } else {
          setHasSignature(false);
          onSignatureChange(null);
        }
      };

      signaturePad.addEventListener('endStroke', handleEnd);

      return () => {
        signaturePad.removeEventListener('endStroke', handleEnd);
      };
    }
  }, [initialSignature, onSignatureChange]);

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setHasSignature(false);
      onSignatureChange(null);
    }
  };

  const downloadSignature = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      const dataURL = signaturePadRef.current.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `firma-${new Date().toISOString().split('T')[0]}.png`;
      link.href = dataURL;
      link.click();
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-md font-medium text-gray-900 dark:text-white">
            Firma del Cliente
          </h3>
          <div className="flex items-center space-x-2">
            {hasSignature && (
              <>
                <button
                  type="button"
                  onClick={downloadSignature}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                  title="Descargar firma"
                >
                  <Download className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="p-2 text-red-600 hover:text-red-800 transition-colors"
                  title="Limpiar firma"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
          <canvas
            ref={canvasRef}
            className="w-full h-32 bg-white dark:bg-gray-100 rounded border border-gray-200 dark:border-gray-300"
            style={{ touchAction: 'none' }}
          />
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {hasSignature ? (
              <span className="flex items-center text-green-600">
                <Check className="h-4 w-4 mr-1" />
                Firma capturada
              </span>
            ) : (
              'Firme en el área de arriba'
            )}
          </p>
          <button
            type="button"
            onClick={clearSignature}
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            Limpiar
          </button>
        </div>
      </div>
    </div>
  );
};

